/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, Grid, Tooltip, Typography, withStyles } from "@material-ui/core";
import { ActionButtons } from "../../../../components/Common";
import { getQueueCallbacksConfig, updateQueueCallbacksConfig } from "../../services/CallbacksService";
import { FormTextField } from "../../../../components";
import { QUEUE_CALLBACKS_CONFIG_TOOLTIPS } from "../../../../utility/toolTipMessages";

const styles = () => ({
	tabContent: {
		padding: 24,
	},
	formContainer: {
		marginTop: 8,
	},
	checkboxLabel: {
		fontSize: 12,
		color: "#808080",
	},
	tooltipIcon: {
		height: 15,
		marginRight: 8,
		color: "#808080",
	},
});

const getNewConfigObject = (queueId) => {
	return {
		queueId,
		enabled: true,
		minEwt: 0,
		minQueuedCount: 0,
		retries: 2,
		retryTimeout: 10,
	};
};

/**
 * This page is used to display and edit the callbacks for a queue.
 * @param {String} queueId The ID of the queue for the callbacks.
 * @param {Function} showSnackbar function to show / hide snackbar
 * @param {boolean} isLoading whether the page is currently loading
 * @param {Function} setIsLoading function to show / hide loading indicator.
 * @param {Function} setTabModified function to show prompt on tab change.
 * @param {object} classes css styling classes
 */
const CallbacksTab = ({ queue, showSnackbar, isLoading, setIsLoading, setTabModified, classes }) => {
	const [config, setConfig] = useState(getNewConfigObject(null));
	const [loadedConfig, setLoadedConfig] = useState(getNewConfigObject(null));
	const [formEdited, setFormEdited] = useState(false);

	const onContentModified = useCallback(
		(state) => {
			setFormEdited(state);
			setTabModified(state);
		},
		[setFormEdited, setTabModified],
	);

	useEffect(() => {
		const init = async () => {
			setIsLoading(true);
			try {
				const loadedConfig = await getQueueCallbacksConfig(queue.Id);
				const defaultConfig = getNewConfigObject(queue.Id);
				const newConfig = loadedConfig ? { ...defaultConfig, ...loadedConfig } : defaultConfig;
				setLoadedConfig(loadedConfig);
				setConfig(newConfig);
				setFormEdited(false);
			} catch (error) {
				showSnackbar("Failed to load configurations.", "error");
			} finally {
				setIsLoading(false);
			}
		};

		queue && init();
	}, [queue, setIsLoading, showSnackbar]);

	const saveConfig = async () => {
		setIsLoading(true);
		try {
			setIsLoading(true);
			await updateQueueCallbacksConfig(queue.Id, config);
			setLoadedConfig(config);
			onContentModified(false);
			setIsLoading(false);
			showSnackbar("Successfully saved callbacks configuration.", "success");
		} catch (error) {
			showSnackbar("Failed to save callbacks configuration.", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfigUpdate = (fieldName, value) => {
		// if value is number (and not boolean), make data type number and not string
		const setValue = !isNaN(value) && typeof value !== "boolean" ? +value : value;
		let newConfig = { ...config, [fieldName]: setValue };
		setConfig(newConfig);

		const lazyCompare = JSON.stringify(newConfig) === JSON.stringify(loadedConfig);
		onContentModified(!lazyCompare);
	};

	const onCancelClick = () => {
		setConfig(loadedConfig);
		onContentModified(false);
	};

	const saveEnabled =
		formEdited &&
		config &&
		config.enabled !== undefined &&
		config.minEwt !== undefined &&
		config.minQueuedCount !== undefined &&
		config.retries !== undefined &&
		config.retryTimeout !== undefined;

	return (
		<div className={classes.tabContent}>
			{!isLoading && (
				<>
					<Grid container alignItems="center" justifyContent="flex-end" wrap="wrap">
						<ActionButtons
							showCancel={true}
							showSave={true}
							className="d-flex justify-content-end && my-2 my-md-0"
							onCancel={onCancelClick}
							onSave={saveConfig}
							disableSave={!saveEnabled}
							disableCancel={!formEdited}
						/>
					</Grid>

					<Grid container spacing={2} className={classes.formContainer}>
						<Grid item xs={12} lg={6}>
							<Typography className={classes.checkboxLabel}>Enabled</Typography>
							<Grid container alignItems="center">
								<Tooltip
									arrow
									interactive
									placement="bottom-start"
									title={QUEUE_CALLBACKS_CONFIG_TOOLTIPS.ENABLED_FIELD}
								>
									<i className={`fas fa-info-circle ${classes.tooltipIcon}`} />
								</Tooltip>
								<Checkbox
									checked={config.enabled}
									onChange={(event) => handleConfigUpdate("enabled", Boolean(event.target.checked))}
								/>
							</Grid>
						</Grid>

						<Grid item xs={12} lg={6}>
							<FormTextField
								fullWidth
								required
								enableDebounce
								type="number"
								label="Minimum EWT"
								valueName="minEwt"
								startingValue={config.minEwt}
								setConfigValue={handleConfigUpdate}
								formEdited={formEdited}
								setEdited={setFormEdited}
								adornment="minutes"
								styleObject={{}}
								toolTipMessage={QUEUE_CALLBACKS_CONFIG_TOOLTIPS.MIN_EWT_FIELD}
								disabled={!config.enabled}
								inputProps={{ min: 0 }}
							/>
						</Grid>

						<Grid item xs={12} lg={6}>
							<FormTextField
								fullWidth
								required
								enableDebounce
								type="number"
								min={0}
								label="Minimum Queued Contacts"
								valueName="minQueuedCount"
								startingValue={config.minQueuedCount}
								setConfigValue={handleConfigUpdate}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{}}
								toolTipMessage={QUEUE_CALLBACKS_CONFIG_TOOLTIPS.MIN_QUEUED_COUNT_FIELD}
								disabled={!config.enabled}
								inputProps={{ min: 0 }}
							/>
						</Grid>

						<Grid item xs={12} lg={6}>
							<FormTextField
								fullWidth
								required
								enableDebounce
								type="number"
								label="Maximum Retries"
								valueName="retries"
								startingValue={config.retries}
								setConfigValue={handleConfigUpdate}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{}}
								toolTipMessage={QUEUE_CALLBACKS_CONFIG_TOOLTIPS.RETRIES_FIELD}
								disabled={!config.enabled}
								inputProps={{ min: 0 }}
							/>
						</Grid>

						<Grid item xs={12} lg={6}>
							<FormTextField
								fullWidth
								required
								enableDebounce
								type="number"
								label="Retry Timeout"
								valueName="retryTimeout"
								startingValue={config.retryTimeout}
								setConfigValue={handleConfigUpdate}
								formEdited={formEdited}
								setEdited={setFormEdited}
								adornment="minutes"
								styleObject={{}}
								toolTipMessage={QUEUE_CALLBACKS_CONFIG_TOOLTIPS.RETRY_TIMEOUT_FIELD}
								disabled={!config.enabled}
								inputProps={{ min: 10, step: 10 }}
							/>
						</Grid>
					</Grid>
				</>
			)}
		</div>
	);
};

export default withStyles(styles)(CallbacksTab);
